// The Phase 2 coverage-option toggle that represents each LOB (region suffix
// appended at lookup). Single source for the LOB↔coverage-key relationship.
const COVERAGE_BY_LOB: Record<string, string> = {
  GL: 'CGL',
  EO: 'EOMock'
};

// Prefix shared by every Munich LOB coverage answer — the CGL/EO toggles, GL's
// add-on sub-coverages, and all their limit fields. WC (Amtrust_*) and broker
// (Coverage_Broker_*) answers fall outside it, so they are never scoped here.
const MUNICH_COVERAGE_PREFIX = 'Coverage_Munich_201_9219_';

// EO owns its toggle (`EOMock`) and its limit field (`MockEOAggregateLimit` —
// note the inverted token the JSON happens to use). GL owns the CGL toggle PLUS
// every add-on sub-coverage and limit (Professional Liability, Limited
// Pollution, Terrorism, …), so GL is the catch-all: any Munich key that is not
// EO-owned belongs to GL.
const isEoOwnedMunichKey = (name: string): boolean =>
  /EOMock|MockEO/.test(name);

const lobOfMunichKey = (name: string): 'EO' | 'GL' =>
  isEoOwnedMunichKey(name) ? 'EO' : 'GL';

// From the given candidate names — SurveyJS `coverageoption` question names OR
// saved answer keys — the Munich coverage names that belong to a LOB OTHER than
// `lobType`. Drives two single-LOB scoping needs, both of which must cover GL's
// whole coverage family (not just the toggle), or GL coverages leak onto EO:
//   1. force-hide the non-endorsed LOB's coverages on a single-LOB endorsement
//      (Phase 2), and
//   2. strip them from a seeded sibling LOB's saved answers before
//      completeApplication (Phase 3).
// Returns [] for an unknown lobType (no-op, never over-scopes).
export const getSiblingMunichCoverageKeys = (
  lobType: string,
  candidateNames: string[]
): string[] => {
  if (!(lobType in COVERAGE_BY_LOB)) return [];
  return candidateNames.filter(
    (name) =>
      name.startsWith(MUNICH_COVERAGE_PREFIX) &&
      lobOfMunichKey(name) !== lobType
  );
};

// The endorsed LOB's own primary coverage toggle (the CGL/EO checkbox the quote
// is based on) from candidate `coverageoption` question names. Matches only the
// LOB-defining toggle via its COVERAGE_BY_LOB token — NOT GL's optional add-on
// sub-coverages, which stay user-toggleable. Returns undefined for an unknown
// lobType or when no matching toggle is present.
export const getPrimaryLobCoverageKey = (
  lobType: string,
  candidateNames: string[]
): string | undefined => {
  const token = COVERAGE_BY_LOB[lobType];
  if (!token) return undefined;
  return candidateNames.find(
    (name) => name.startsWith(MUNICH_COVERAGE_PREFIX) && name.includes(token)
  );
};
